/**
 * The mission catalog: what the launch surface knows about missions before a
 * package is loaded (task 9.5).
 *
 * The shell holds no mission list of its own. Everything the mission select
 * screen shows — order, titles, premises, unlock prerequisites — arrives here,
 * so adding a mission is a content and catalog change with no shell edit
 * (`CONTRIBUTING.md`, the data/code boundary rule).
 *
 * Fetching, caching, digest verification and `minEngineVersion` refusal are the
 * offline delivery layer's job and land with it; this module is the shape and
 * the pure queries over it.
 */

/**
 * What kind of thing an entry is.
 *
 * `campaign` is everything that existed before the field did, which is why it
 * is the default for an entry that omits it: an older catalog read by a newer
 * client is a catalog of campaign missions, and a newer catalog read by an
 * older client degrades to "it is a mission" (design D4).
 */
export type MissionKind = 'campaign' | 'tutorial';

export const DEFAULT_MISSION_KIND: MissionKind = 'campaign';

export interface CatalogEntry {
  readonly id: string;
  readonly title: string;
  readonly company: string;
  /** One line for the mission select list. */
  readonly premise: string;
  readonly order: number;
  /** Package location, resolved relative to the catalog's own URL. */
  readonly url: string;
  readonly packVersion: string;
  /** Lowercase hex SHA-256 of the package bytes. */
  readonly sha256: string;
  /** Mission ids that must be completed before this one unlocks. */
  readonly requires: readonly string[];
  readonly minEngineVersion: string;
  /** `campaign` unless the entry says otherwise. Never absent after parsing. */
  readonly kind: MissionKind;
}

export interface Catalog {
  readonly catalogVersion: string;
  readonly missions: readonly CatalogEntry[];
}

/** True for an entry the campaign's rules do not apply to (design D4, D9). */
export function isTutorial(entry: CatalogEntry): boolean {
  return entry.kind === 'tutorial';
}

/**
 * A tutorial first, then catalog order, then id, so the list is stable however
 * the file is written.
 *
 * The kind is the first key rather than trusting `order: 0`: "listed first" is
 * a requirement of the `game-shell` spec, and a catalog that numbered its
 * tutorial fifth would otherwise bury the one entry a new player needs. The
 * catalog's own order still decides everything within a kind.
 */
export function missionsInOrder(catalog: Catalog): readonly CatalogEntry[] {
  return [...catalog.missions].sort(
    (a, b) =>
      Number(isTutorial(b)) - Number(isTutorial(a)) ||
      a.order - b.order ||
      a.id.localeCompare(b.id),
  );
}

export function entryById(catalog: Catalog, missionId: string): CatalogEntry | null {
  return catalog.missions.find((mission) => mission.id === missionId) ?? null;
}

export interface LockState {
  readonly locked: boolean;
  /** The unmet prerequisites, in catalog order, for naming them to the player. */
  readonly missing: readonly CatalogEntry[];
}

const UNLOCKED: LockState = { locked: false, missing: [] };

/**
 * A mission is locked while any `requires` entry is uncompleted. Prerequisites
 * are returned as catalog entries rather than ids so the screen can name them,
 * which the spec requires ("shown locked with the prerequisite mission named").
 *
 * A tutorial is never locked, whatever it declares. It is the first thing a new
 * player meets and it gates nothing, so a `requires` on a tutorial entry is a
 * catalog mistake rather than an instruction — and the one way a player could
 * be shut out of the walkthrough is a state this function refuses to produce.
 */
export function lockStateOf(
  catalog: Catalog,
  entry: CatalogEntry,
  completedIds: readonly string[],
): LockState {
  if (isTutorial(entry)) return UNLOCKED;
  const missing = entry.requires
    .filter((id) => !completedIds.includes(id))
    .map((id) => entryById(catalog, id))
    .filter((found): found is CatalogEntry => found !== null);
  const unresolved = entry.requires.filter((id) => !completedIds.includes(id));
  return { locked: unresolved.length > 0, missing };
}

/** Rejects a catalog whose shape would fail later, at a point with no context. */
export function parseCatalog(value: unknown): Catalog {
  const raw = value as Partial<Catalog> | null;
  if (!raw || typeof raw !== 'object' || !Array.isArray(raw.missions)) {
    throw new Error('Catalog is missing its `missions` array.');
  }
  for (const mission of raw.missions) {
    for (const field of ['id', 'title', 'url', 'packVersion'] as const) {
      if (typeof mission[field] !== 'string' || mission[field].length === 0) {
        throw new Error(`Catalog entry is missing \`${field}\`.`);
      }
    }
  }
  return {
    catalogVersion: typeof raw.catalogVersion === 'string' ? raw.catalogVersion : '0',
    missions: raw.missions.map((mission) => ({
      ...mission,
      company: mission.company ?? '',
      premise: mission.premise ?? '',
      order: typeof mission.order === 'number' ? mission.order : 0,
      sha256: mission.sha256 ?? '',
      requires: mission.requires ?? [],
      minEngineVersion: mission.minEngineVersion ?? '0.0.0',
      // An unrecognised kind is read as a campaign mission rather than
      // refused: a future catalog naming a kind this build has never heard of
      // must still list the missions it does understand.
      kind: mission.kind === 'tutorial' ? 'tutorial' : DEFAULT_MISSION_KIND,
    })),
  };
}
